import React, { useState } from 'react'
import { Scale, Loader2, ChevronDown, ChevronRight, CheckCircle2, XCircle, MinusCircle, CircleDot } from 'lucide-react'
import { useTruthLens } from '../hooks/useResearch'
import { canEmbed } from './VideoPlayer'
import { sourceLink } from './docFormat'
import usePlayerStore from '../stores/playerStore'

// Truth lens (doc/plan-truth-lens.md): per-claim corpus consensus under an Ask
// answer. Each claim gets a consensus bar (videos that affirm / contradict /
// are mixed / discuss without stance) and expandable, time-linked evidence.
// The money view: a claim with both sides shows supports vs contradicts
// side-by-side.

const STANCE = {
  supports: { icon: CheckCircle2, cls: 'text-emerald-600 dark:text-emerald-400', label: 'supports' },
  contradicts: { icon: XCircle, cls: 'text-red-600 dark:text-red-400', label: 'contradicts' },
  discusses: { icon: MinusCircle, cls: 'text-ink-soft dark:text-mist/50', label: 'no stance' },
}

const BAR = [
  ['affirms', 'bg-emerald-500'],
  ['contradicts', 'bg-red-500'],
  ['mixed', 'bg-amber-500'],
  ['silent', 'bg-parchment-border dark:bg-midnight-600'],
]

function ConsensusBar({ consensus }) {
  const total = BAR.reduce((s, [k]) => s + (consensus?.[k] ?? 0), 0)
  if (!total) {
    return <span className="text-xs italic text-ink-soft dark:text-mist/50">no relevant sources</span>
  }
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-2 w-40 overflow-hidden rounded-full bg-parchment-200 dark:bg-midnight-700">
        {BAR.map(([k, cls]) =>
          consensus[k] ? (
            <div key={k} className={cls} style={{ width: `${(consensus[k] / total) * 100}%` }} />
          ) : null
        )}
      </div>
      <span className="text-xs text-ink-soft dark:text-mist/60 whitespace-nowrap">
        {consensus.affirms} affirm · {consensus.contradicts} contradict
        {consensus.mixed ? ` · ${consensus.mixed} mixed` : ''}
        {consensus.silent ? ` · ${consensus.silent} no stance` : ''}
      </span>
    </div>
  )
}

function EvidenceRow({ e }) {
  const openPlayer = usePlayerStore((s) => s.openPlayer)
  const st = STANCE[e.stance] ?? STANCE.discusses
  const Icon = st.icon
  const t = e.start_time_formatted
  const playable = canEmbed(e)
  const href = sourceLink(e, e.start_time)
  return (
    <div className="flex gap-2 py-1.5">
      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${st.cls}`} />
      <div className="min-w-0 flex-1">
        <div className="text-xs text-ink dark:text-mist truncate">
          {e.title || e.source}
          {t &&
            (playable ? (
              <button
                onClick={() => openPlayer({ ...e, start: e.start_time })}
                className="ml-2 font-mono text-[11px] text-gold-deep dark:text-gold/80 hover:underline"
                title="Watch this moment"
              >
                ▶ {t}
              </button>
            ) : href ? (
              <a href={href} target="_blank" rel="noreferrer" className="ml-2 font-mono text-[11px] text-gold-deep dark:text-gold/80 hover:underline">
                ▶ {t}
              </a>
            ) : (
              <span className="ml-2 font-mono text-[11px] text-ink-soft dark:text-mist/40">{t}</span>
            ))}
        </div>
        {e.quote && <p className="text-xs italic text-ink-soft dark:text-mist/60">„{e.quote}"</p>}
        {e.rationale && <p className="text-[11px] text-ink-soft/80 dark:text-mist/40">{e.rationale}</p>}
      </div>
    </div>
  )
}

function ClaimRow({ item }) {
  const [open, setOpen] = useState(false)
  const supports = (item.evidence || []).filter((e) => e.stance === 'supports')
  const contradicts = (item.evidence || []).filter((e) => e.stance === 'contradicts')
  const discusses = (item.evidence || []).filter((e) => e.stance === 'discusses')
  const conflict = supports.length > 0 && contradicts.length > 0
  return (
    <div className="rounded-lg border border-parchment-border bg-white/50 p-3 dark:border-midnight-600 dark:bg-midnight-800/40">
      <button onClick={() => setOpen((v) => !v)} className="w-full text-left">
        <div className="flex items-start gap-2">
          {open ? (
            <ChevronDown className="w-3.5 h-3.5 mt-1 shrink-0 text-ink-soft dark:text-mist/50" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 mt-1 shrink-0 text-ink-soft dark:text-mist/50" />
          )}
          <div className="min-w-0 flex-1 space-y-1.5">
            <p className="text-sm text-ink dark:text-mist">{item.claim}</p>
            <ConsensusBar consensus={item.consensus} />
          </div>
        </div>
      </button>
      {open && (
        <div className="mt-2 pl-6 space-y-2">
          {conflict ? (
            // the money view: both sides, face to face
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-emerald-300/50 bg-emerald-50/50 p-2 dark:border-emerald-800/50 dark:bg-emerald-900/10">
                <div className="text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-1">Affirming</div>
                {supports.map((e, i) => <EvidenceRow key={i} e={e} />)}
              </div>
              <div className="rounded-md border border-red-300/50 bg-red-50/50 p-2 dark:border-red-800/50 dark:bg-red-900/10">
                <div className="text-[11px] uppercase tracking-wide text-red-700 dark:text-red-300 mb-1">Contradicting</div>
                {contradicts.map((e, i) => <EvidenceRow key={i} e={e} />)}
              </div>
            </div>
          ) : (
            <>
              {supports.map((e, i) => <EvidenceRow key={`s${i}`} e={e} />)}
              {contradicts.map((e, i) => <EvidenceRow key={`c${i}`} e={e} />)}
            </>
          )}
          {discusses.length > 0 && (
            <details className="text-xs text-ink-soft dark:text-mist/50">
              <summary className="cursor-pointer">
                {discusses.length} passage(s) discuss the topic without a stance
              </summary>
              {discusses.map((e, i) => <EvidenceRow key={i} e={e} />)}
            </details>
          )}
        </div>
      )}
    </div>
  )
}

// Button + panel pair for an Ask answer.
export function TruthLensButton({ onClick, active, pending }) {
  return (
    <button
      onClick={onClick}
      disabled={pending}
      title="Truth lens — how does the corpus weigh this answer's claims?"
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border transition ${
        active
          ? 'border-gold/60 bg-gold/10 text-gold-deep dark:text-gold'
          : 'border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold'
      } disabled:opacity-40`}
    >
      {pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scale className="w-3.5 h-3.5" />}
      Truth lens
    </button>
  )
}

// v1.5 — verify a FREE-STANDING claim against the project corpus, no Ask
// answer needed (the engine accepts explicit `claims`). Lives at the top of
// the Ask mode: type a statement, get per-video consensus.
export function ClaimVerifier({ project }) {
  const lens = useTruthLens()
  const [claim, setClaim] = useState('')
  const [open, setOpen] = useState(false)
  const run = () =>
    claim.trim() && project && lens.mutate({ project, claims: [claim.trim()], answer: claim.trim() })

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs text-ink-soft hover:text-gold-deep dark:text-mist/50 dark:hover:text-gold"
        title="Weigh any statement against this project's sources (no question needed)"
      >
        <Scale className="w-3.5 h-3.5" /> Verify a claim…
      </button>
    )
  }
  return (
    <div className="rounded-xl border border-parchment-border bg-white/60 p-3 space-y-2 dark:border-midnight-600 dark:bg-midnight-800/50">
      <div className="flex flex-wrap items-center gap-2">
        <Scale className="w-4 h-4 text-gold-deep dark:text-gold shrink-0" />
        <input
          autoFocus
          value={claim}
          onChange={(e) => setClaim(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && run()}
          placeholder="State a claim to weigh against the sources…"
          className="flex-1 min-w-[240px] h-9 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
        />
        <button
          onClick={run}
          disabled={!claim.trim() || !project || lens.isPending}
          className="h-9 px-3 rounded-md text-xs border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 dark:text-gold"
        >
          {lens.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Verify'}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="text-xs text-ink-soft hover:text-ink dark:text-mist/50"
        >
          close
        </button>
      </div>
      <TruthLensPanel lens={lens} />
    </div>
  )
}

export default function TruthLensPanel({ lens }) {
  if (lens.isPending) {
    return (
      <div className="mt-3 flex items-center gap-2 text-sm text-ink-soft dark:text-mist/60">
        <Loader2 className="w-4 h-4 animate-spin" />
        Weighing the answer's claims against the corpus… (retrieval + stance analysis)
      </div>
    )
  }
  if (lens.isError) {
    return <p className="mt-3 text-sm text-red-600">{lens.error?.message}</p>
  }
  const claims = lens.data?.claims ?? []
  if (!claims.length) return null
  return (
    <div className="mt-3 space-y-2">
      <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold flex items-center gap-1.5">
        <Scale className="w-3.5 h-3.5" /> Truth lens
        {lens.data?.cached && <span className="text-[10px] normal-case tracking-normal text-ink-soft dark:text-mist/40">(cached)</span>}
      </h3>
      {claims.map((c, i) => (
        <ClaimRow key={i} item={c} />
      ))}
      <p className="text-[11px] text-ink-soft/80 dark:text-mist/40">
        Consensus counts are per video within this project — a corpus mirror, not external fact-checking.
      </p>
    </div>
  )
}
